package com.sweetlime.bingeamigo.presentation.features.tvdetail

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.filled.StarBorder
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.border
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.sweetlime.bingeamigo.model.SeasonSummary
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.model.TvShowDetail
import com.sweetlime.bingeamigo.model.UserReview
import com.sweetlime.bingeamigo.model.toLibraryTvShow
import com.sweetlime.bingeamigo.model.types.ContentType
import com.sweetlime.bingeamigo.presentation.components.DetailRow
import com.sweetlime.bingeamigo.presentation.components.FriendReviewCard
import com.sweetlime.bingeamigo.presentation.components.InfoChip
import com.sweetlime.bingeamigo.presentation.components.RateReviewSheet
import com.sweetlime.bingeamigo.presentation.components.TvCompletedBadge
import com.sweetlime.bingeamigo.presentation.components.TvFavoriteButton
import com.sweetlime.bingeamigo.presentation.components.TvTrackingButton
import com.sweetlime.bingeamigo.presentation.features.library.TvLibraryViewModel
import com.sweetlime.bingeamigo.presentation.features.review.FriendReviewsViewModel
import com.sweetlime.bingeamigo.presentation.features.review.ReviewViewModel
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.util.computeDisplayRating
import com.sweetlime.bingeamigo.util.format1f
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun TvShowDetailContent(
    tvShow: TvShow,
    onBackClick: () -> Unit,
    onSeasonClick: (SeasonSummary, String, Int) -> Unit,
    viewModel: TvShowDetailViewModel = koinViewModel(),
    reviewViewModel: ReviewViewModel = koinViewModel(),
    tvLibraryViewModel: TvLibraryViewModel = koinViewModel(),
    friendReviewsViewModel: FriendReviewsViewModel = koinViewModel()
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val reviewUiState by reviewViewModel.uiState.collectAsStateWithLifecycle()
    val contentSummary by reviewViewModel.contentSummary.collectAsStateWithLifecycle()
    val tracking by tvLibraryViewModel.tracking.collectAsStateWithLifecycle()
    val completed by tvLibraryViewModel.completed.collectAsStateWithLifecycle()
    val friendReviewsState by friendReviewsViewModel.state.collectAsStateWithLifecycle()
    val userReview = (tracking + completed).find { it.id == tvShow.id }?.userReview
        ?: reviewUiState.existingReview?.review
    var showRateSheet by remember { mutableStateOf(false) }

    LaunchedEffect(tvShow.id) {
        viewModel.load(tvShow.id)
        friendReviewsViewModel.load(ContentType.SHOW, tvShow.id.toString())
    }
    LaunchedEffect(reviewUiState.saved) {
        if (reviewUiState.saved) {
            showRateSheet = false
            reviewViewModel.reset()
        }
    }

    if (showRateSheet) {
        val libraryTvShow = tvShow.toLibraryTvShow()
        RateReviewSheet(
            title = tvShow.name,
            initialRating = reviewUiState.existingReview?.rating ?: 0.0,
            initialReview = reviewUiState.existingReview?.review,
            isLoading = reviewUiState.isLoading,
            isSaving = reviewUiState.isSaving,
            onDismiss = {
                showRateSheet = false
                reviewViewModel.reset()
            },
            onSubmit = { rating, text ->
                reviewViewModel.submitShowReview(libraryTvShow, rating, text)
            }
        )
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF121212))
    ) {
        when (state) {
            is TvShowDetailState.Loading -> {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
            }
            is TvShowDetailState.Error -> {
                Text(
                    text = (state as TvShowDetailState.Error).message,
                    color = Color.White,
                    modifier = Modifier.align(Alignment.Center)
                )
            }
            is TvShowDetailState.Success -> {
                TvShowDetailLoaded(
                    tvShow = tvShow,
                    detail = (state as TvShowDetailState.Success).detail,
                    userRating = reviewUiState.existingReview?.rating ?: 0.0,
                    userReview = userReview,
                    communityAvgRating = contentSummary.avgRating,
                    friendReviews = friendReviewsState.reviews,
                    likedReviewIds = friendReviewsState.likedReviewIds,
                    onLikeReview = { friendReviewsViewModel.toggleLike(it) },
                    onSeasonClick = onSeasonClick,
                    onRateClick = {
                        reviewViewModel.load(ContentType.SHOW, tvShow.id.toString())
                        showRateSheet = true
                    }
                )
            }
        }

        // Static top bar — always visible, never scrolls
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .padding(12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            IconButton(
                onClick = onBackClick,
                modifier = Modifier.background(Color.Black.copy(alpha = 0.5f), CircleShape)
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                    contentDescription = "Back",
                    tint = Color.White
                )
            }
            if (state is TvShowDetailState.Success) {
                TvFavoriteButton(
                    libraryTvShow = tvShow.toLibraryTvShow()
                )
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun TvShowDetailLoaded(
    tvShow: TvShow,
    detail: TvShowDetail,
    userRating: Double,
    userReview: String?,
    communityAvgRating: Double,
    friendReviews: List<UserReview>,
    likedReviewIds: Set<String> = emptySet(),
    onLikeReview: (UserReview) -> Unit = {},
    onSeasonClick: (SeasonSummary, String, Int) -> Unit,
    onRateClick: () -> Unit
) {
    val backdropUrl = "https://image.tmdb.org/t/p/w780${detail.backdropPath ?: tvShow.backdropPath}"
    val posterUrl = "https://image.tmdb.org/t/p/w500${detail.posterPath ?: tvShow.posterPath}"
    val libraryTvShow = tvShow.toLibraryTvShow()

    LazyColumn {

        // Hero section
        item {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(min = 380.dp)
            ) {
                KamelImage(
                    resource = asyncPainterResource(backdropUrl),
                    contentDescription = detail.name,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.matchParentSize()
                )

                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .background(
                            Brush.verticalGradient(
                                colorStops = arrayOf(
                                    0.25f to Color.Transparent,
                                    1.0f to Color(0xFF121212)
                                )
                            )
                        )
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 130.dp, start = 20.dp, end = 20.dp, bottom = 20.dp),
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalAlignment = Alignment.Top
                ) {
                    Card(
                        shape = RoundedCornerShape(16.dp),
                        elevation = CardDefaults.cardElevation(8.dp)
                    ) {
                        KamelImage(
                            resource = asyncPainterResource(posterUrl),
                            contentDescription = detail.name,
                            contentScale = ContentScale.Crop,
                            modifier = Modifier
                                .width(110.dp)
                                .height(165.dp)
                        )
                    }

                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            text = detail.name,
                            color = Color.White,
                            style = MaterialTheme.typography.headlineSmall,
                            fontWeight = FontWeight.Bold,
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis
                        )

                        TvCompletedBadge(libraryTvShow)

                        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            if (detail.firstAirDate.length >= 4) {
                                InfoChip(text = detail.firstAirDate.take(4))
                            }
                            InfoChip(text = "⭐ ${computeDisplayRating(detail.voteAverage, communityAvgRating.takeIf { it > 0.0 }).format1f()}")
                            InfoChip(text = detail.status)
                        }

                        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            InfoChip(text = "${detail.numberOfSeasons} seasons")
                            InfoChip(text = "${detail.numberOfEpisodes} eps")
                            detail.genres.firstOrNull()?.let {
                                InfoChip(text = it.name)
                            }
                        }

                        Column(modifier = Modifier.width(200.dp)) {
                            TvTrackingButton(libraryTvShow)
                            Button(
                                onClick = onRateClick,
                                modifier = Modifier.fillMaxWidth(),
                                colors = if (userRating > 0.0)
                                    ButtonDefaults.buttonColors(containerColor = BrandColors.Primary, contentColor = Color.Black)
                                else
                                    ButtonDefaults.buttonColors(containerColor = Color.Transparent, contentColor = Color.White),
                                border = if (userRating <= 0.0) BorderStroke(1.dp, Color.White.copy(alpha = 0.4f)) else null
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Icon(
                                        if (userRating > 0.0) Icons.Filled.Star else Icons.Filled.StarBorder,
                                        contentDescription = null
                                    )
                                    Spacer(Modifier.width(6.dp))
                                    Text(
                                        text = if (userRating > 0.0) "${userRating.format1f()} / 5" else "Rate"
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        // Tagline
        if (detail.tagline.isNotBlank()) {
            item {
                Text(
                    text = "\"${detail.tagline}\"",
                    color = Color.Gray,
                    style = MaterialTheme.typography.bodyMedium,
                    fontStyle = androidx.compose.ui.text.font.FontStyle.Italic,
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp)
                )
            }
        }

        // Overview
        item {
            Column(
                modifier = Modifier.padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text(
                    text = "Overview",
                    color = Color.White,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text = detail.overview.ifBlank { "No overview available." },
                    color = Color.LightGray,
                    style = MaterialTheme.typography.bodyLarge,
                    lineHeight = 24.sp
                )
            }
        }

        // Details
        item {
            Column(
                modifier = Modifier.padding(horizontal = 20.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                DetailRow(label = "Original Name", value = detail.originalName)
                DetailRow(label = "Language", value = detail.originalLanguage.uppercase())
                DetailRow(label = "Votes", value = detail.voteCount.toString())
                if (detail.lastAirDate.isNotBlank()) {
                    DetailRow(label = "Last Air Date", value = detail.lastAirDate)
                }
            }
        }

        // Seasons
        if (detail.seasons.isNotEmpty()) {
            item {
                Text(
                    text = "Seasons",
                    color = Color.White,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(start = 20.dp, top = 28.dp, bottom = 12.dp)
                )
            }

            val totalEpisodes = detail.seasons.filter { it.seasonNumber > 0 }.sumOf { it.episodeCount }
            items(detail.seasons.filter { it.seasonNumber > 0 }) { season ->
                SeasonRow(
                    season = season,
                    showPosterFallback = posterUrl,
                    onClick = { onSeasonClick(season, posterUrl, totalEpisodes) }
                )
            }
        }

        // Your review
        if (!userReview.isNullOrBlank()) {
            item {
                HorizontalDivider(
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
                    color = Color.White.copy(alpha = 0.08f)
                )
                Column(modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp)) {
                    Text(
                        text = "Your review",
                        color = Color.Gray,
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = userReview,
                        color = Color.LightGray,
                        style = MaterialTheme.typography.bodyMedium,
                        lineHeight = MaterialTheme.typography.bodyMedium.lineHeight
                    )
                }
            }
        }

        // Friends' reviews
        if (friendReviews.isNotEmpty()) {
            item {
                HorizontalDivider(
                    modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
                    color = Color.White.copy(alpha = 0.08f)
                )
                Text(
                    text = "Friends' Reviews",
                    color = Color.White,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(start = 20.dp, end = 20.dp, bottom = 10.dp)
                )
            }
            items(friendReviews) { review ->
                FriendReviewCard(
                    review = review,
                    isLiked = review.docId in likedReviewIds,
                    onLikeClick = { onLikeReview(review) },
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp)
                )
            }
        }

        item {
            Spacer(modifier = Modifier.height(120.dp))
        }
    }
}

@Composable
private fun SeasonRow(season: SeasonSummary, showPosterFallback: String, onClick: () -> Unit) {
    val posterUrl = if (season.posterPath != null) {
        "https://image.tmdb.org/t/p/w185${season.posterPath}"
    } else {
        showPosterFallback
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 6.dp)
            .clip(RoundedCornerShape(18.dp))
            .background(BrandColors.Card)
            .border(1.dp, Color(0xFF2A2A2E), RoundedCornerShape(18.dp))
            .clickable { onClick() }
            .padding(12.dp),
        horizontalArrangement = Arrangement.spacedBy(14.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        KamelImage(
            resource = asyncPainterResource(posterUrl),
            contentDescription = season.name,
            contentScale = ContentScale.Crop,
            modifier = Modifier
                .size(56.dp)
                .clip(RoundedCornerShape(8.dp))
                .aspectRatio(2f / 3f)
        )

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = season.name,
                color = Color.White,
                fontWeight = FontWeight.SemiBold,
                style = MaterialTheme.typography.bodyLarge
            )
            val year = season.airDate.take(4)
            val episodeText = "${season.episodeCount} episodes"
            Text(
                text = if (year.isNotBlank()) "$year · $episodeText" else episodeText,
                color = Color.Gray,
                style = MaterialTheme.typography.bodySmall
            )
            if (season.voteAverage > 0.0) {
                Text(
                    text = "⭐ ${(season.voteAverage / 2).format1f()}",
                    color = Color.Gray,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }
    }
}